import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { Card, Row, Col, Bubble } from 'tea-component';
// import TipContext from '@home/components/TipContextComponents/TipContext';
import Loading from '@intelligence/components/Loading';
import SteamLineReviews from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPIOverview/components/SteamLineReviews';
import CommonChart from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPIOverview/components/CommonChart';
import OverviewCard from '@intelligence/components/OverviewCard';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import useCalcHasEmptyMetric from '../../hooks/useCalcHasEmptyMetric';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { toDateTypeLy } from '@intelligence/utils/common';
import { analysisMetricKey } from '@intelligence/v3/utils/metric';
import { calcIsEmptyOverviewCard } from '../../../utils';
import TimeType from '@intelligence/components/TimeType';
import { DAY } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { COMPETITOR_GUIDE_CLASSNAMES } from '@intelligence/components/UserGuide/Competitor';
import SteamHourlyCCU from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPIOverview/components/SteamHourlyCCU';

import { DATASOURCE_NAME, DataSourceBubbleInfo } from '@intelligence/components/DataSourceComponents';

const STORE_RANK_METRICS = ['steam top wish lists', 'steam top sellers'];
const getIsStoreRankMetric = (metric?: string) => !!metric && STORE_RANK_METRICS.includes(metric);

// 需要替换为 Source Steam 数据源的指标列表（PCU/ACU 走 Steam 官方数据源）
const SOURCE_STEAM_METRICS = ['pcu', 'acu'];
// 需要替换为 Gamalytic 数据源的指标列表
const GAMALYTIC_METRICS = ['est_cumulative_units', 'wishlists_total', 'wishlists', 'est_price'];
const DAILY_WISHLIST_METRIC_KEY = 'wishlists';
const TITLE_INFO_TIP_LOCALE_KEY_BY_METRIC: Record<string, string> = {
  [DAILY_WISHLIST_METRIC_KEY]: 'dbi_info_daily_wishlist_after_launch',
};

const getTitleInfoTipLocaleKey = (metricKey?: string) => {
  return TITLE_INFO_TIP_LOCALE_KEY_BY_METRIC[analysisMetricKey(metricKey ?? '')?.metric ?? ''];
};

const SteamDataContent = (props: any) => {
  const {
    loading,
    gameId,
    setActiveId,
    setActiveSegment,
    activeId,
    moduleName,
    conditionMarket,
    entityType,
    isOperateRegion,
    combineGameIdList,
    basicInfo,
    showNoDataMetricCard,
    setSteamDataHasEmptyMetric,
    setSteamDataHasEmptyLineData,
    getSteamCardData,
    steamLoading,
    steamDataList,
    steamRankLoading: rankLoading,
    kpiPlayerData,
    kpiPlaytimeData,
    steamHourlyCcuRawItem,
    isGamalyticSource = false,
  } = props;

  const { locale, formatMessage } = useIntlExtend();

  const hasSteamPlatform = useMemo<boolean>(() => {
    return !!basicInfo?.platforms?.includes('steam');
  }, [basicInfo]);

  const linkUrl = useMemo<string>(() => {
    const idList = combineGameIdList?.filter((game: any) => game?.entity_type === 'pc') || [];
    if (!idList?.length) return '';
    const { id } = idList[0] || {};
    return `/opinion/SteamPerformance/SteamRatings?gameid=${id}`;
  }, [combineGameIdList]);

  const [steamDateType, setSteamDateType] = useState(DAY);

  useCalcHasEmptyMetric({
    executable: !(loading || steamLoading) && hasSteamPlatform,
    metrics: steamDataList,
    excludes: STORE_RANK_METRICS,
    setHasEmptyMetric: setSteamDataHasEmptyMetric,
  });

  const onChangeTimeType = (value: any) => {
    setSteamDateType(value);
    getSteamCardData(toDateTypeLy[value]);
  };

  const getSteamLineReviews = () => (
    <SteamLineReviews steamDateType={steamDateType} gameId={gameId} combineGameIdList={combineGameIdList} />
  );

  useEffect(() => {
    getSteamCardData(toDateTypeLy[steamDateType]);
  }, []);

  if (!hasSteamPlatform) return null;
  return (
    <Card className="dbi-performance-summary dbi-kpioverview-card">
      <Card.Body
        title={
          <div className="performance-felx">
            <Bubble
              content={
                <div>
                  <DataSourceBubbleInfo
                    comesFormDom={
                      <>
                        {formatMessage(
                          { id: 'dbi_combine_steam_Data_tips_and' },
                          {
                            steam: (
                              <>
                                <img
                                  className="platform-title-icon"
                                  style={{
                                    height: '20px',
                                    width: '20px',
                                    margin: '0 4px',
                                    verticalAlign: 'middle',
                                  }}
                                  src={getDataSourceIcon('steam')}
                                />
                                {DATASOURCE_NAME.steam}
                              </>
                            ),
                            vginsights: (
                              <>
                                <img
                                  className="platform-title-icon"
                                  style={{
                                    height: '20px',
                                    width: '20px',
                                    margin: '0 4px',
                                    verticalAlign: 'middle',
                                  }}
                                  src={isGamalyticSource ? getDataSourceIcon('gamalytic') : getDataSourceIcon('vginsights')}
                                />
                                {isGamalyticSource ? DATASOURCE_NAME.gamalytic : DATASOURCE_NAME.vginsights}
                              </>
                            )
                          } as any,
                        )}
                        {isGamalyticSource || (
                          <>
                            <div>{formatMessage({ id: 'dbi_combine_steam_Data_tips1' })}</div>
                            <div>{formatMessage({ id: 'dbi_combine_steam_Data_tips2' })}</div>
                            <div>{formatMessage({ id: 'dbi_combine_steam_Data_tips3' })}</div>
                            <div>{formatMessage({ id: 'dbi_combine_steam_Data_tips4' })}</div>
                          </>
                        )}
                        {isGamalyticSource && (
                          <>
                            <div>{formatMessage({ id: 'dbi_combine_steam_Data_gamalytic_tips1' })}</div>
                            <div>{formatMessage({ id: 'dbi_combine_steam_Data_gamalytic_tips2' })}</div>
                            <div>{formatMessage({ id: 'dbi_combine_steam_Data_tips3' })}</div>
                            <div>{formatMessage({ id: 'dbi_combine_steam_Data_gamalytic_tips4' })}</div>
                          </>
                        )}
                      </>
                    }
                    metricKey={
                      'Steam PCU,Total Units Sold,Lifetime Wishlist,Steam ACU,Top Sellers,Daily Wishlist,Top Wish Lists,Price,Time Spent,Players by country'
                    }
                    dataSource={isGamalyticSource ? 'Steam,gamalytic' : 'Steam,vginsights'}
                    entityType={entityType}
                  />
                </div>
              }
            >
              <div className="performance-felx">
                <img src={getDataSourceIcon('steam')} className="dbi-pcGame-select-icon" style={{ height: '20px', width: '20px' }} />
              </div>
            </Bubble>
            <span className="performance-felx-text">{formatMessage({ id: 'dbi_combine_steam_Data' })}</span>
            {/* <TipContext
                  className="icon-top"
                  tipText={formatMessage({ id: 'dbi_combine_steam_Data_tips' })}
                /> */}
          </div>
        }
      >
        <TimeType
          lightTheme={false}
          locale={locale}
          onConfirm={(val: any) => {
            onChangeTimeType(val);
          }}
          defaultValue={DAY}
          value={steamDateType || ''}
        />

        {steamLoading ? (
          <Loading content={formatMessage({ id: 'common_loading' })} style={{ height: 656, color: '#444' }} />
        ) : (
          <>
            <SteamHourlyCCU
              gameId={gameId}
              entityType={entityType}
              hourlyCcuRawItem={steamHourlyCcuRawItem}
              dateType={steamDateType}
            />
            <Row>
                  {steamDataList?.map((item: any, index: number) => {
                    // 过滤掉 hourly_ccu 指标，因为它已经在 SteamHourlyCCU 组件中展示了
                    if (item?.metricUserGuide === 'source_steam__hourly_ccu') return null;
                    if (item.titleIcon === 'viewers') return null;
                    const { data_source: originalDataSource, metricUserGuide: originalMetricUserGuide } = item || {};

                    // 所有业务逻辑判断都使用原始数据，不受 isGamalyticSource 影响
                    const isStoreRankMetric = getIsStoreRankMetric(originalDataSource);
                    if (!showNoDataMetricCard && !isStoreRankMetric && calcIsEmptyOverviewCard(item)) return null;
                    const isDailyWishlistMetric = analysisMetricKey(originalMetricUserGuide ?? '')?.metric === DAILY_WISHLIST_METRIC_KEY;
                    const isDailyOnlyStoreRankUnavailable =
                      (originalDataSource === 'steam top wish lists' && steamDateType !== DAY) ||
                      (originalDataSource === 'steam top sellers' && steamDateType === 'month');
                    const storeRankNoDataTextKey = isDailyOnlyStoreRankUnavailable
                      ? 'dbi_no_data_available_store_rank_daily_only'
                      : originalDataSource === 'steam top sellers'
                        ? 'dbi_no_data_available_top_100_seller_rank'
                        : 'dbi_no_data_available_store_rank';

                    // 在 PC & Console Games 页面，PCU/ACU 走 source_steam，其余历史指标仍走 gamalytic
                    const shouldReplaceWithSourceSteam =
                      isGamalyticSource && SOURCE_STEAM_METRICS.includes(originalMetricUserGuide);
                    const shouldReplaceWithGamalytic =
                      isGamalyticSource && GAMALYTIC_METRICS.includes(originalMetricUserGuide);
                    let displayItem = item; // 默认使用原始数据
                    let itemDataSource = originalDataSource;
                    let itemMetricUserGuide = originalMetricUserGuide;

                    if (shouldReplaceWithSourceSteam) {
                      const sourceSteamKey = `source_steam__${originalMetricUserGuide}`;
                      // 在 steamDataList 中查找对应的 source_steam 数据
                      const sourceSteamItem = steamDataList?.find(
                        (dataItem: any) =>
                          dataItem?.data_source === 'source_steam' &&
                          dataItem?.metricUserGuide === sourceSteamKey,
                      );

                      if (sourceSteamItem) {
                        displayItem = sourceSteamItem;
                      } else {
                        // 未返回 source_steam 数据时，构造空卡片结构，避免 UI 抖动
                        displayItem = {
                          ...item,
                          baseIndicators: {},
                          numArrowData: {},
                          lineData: {},
                          rankData: {},
                          data_source: 'source_steam',
                          metricUserGuide: sourceSteamKey,
                        };
                      }
                      itemDataSource = 'source_steam';
                      itemMetricUserGuide = sourceSteamKey;
                    } else if (shouldReplaceWithGamalytic) {
                      // 在 steamDataList 中查找对应的 gamalytic 数据
                      const gamalyticItem = steamDataList?.find(
                        (dataItem: any) =>
                          dataItem?.data_source === 'gamalytic' &&
                          dataItem?.metricUserGuide === `gamalytic__${originalMetricUserGuide}`,
                      );

                      if (gamalyticItem) {
                        // 如果找到了 gamalytic 数据，则使用该数据
                        displayItem = gamalyticItem;
                        itemDataSource = 'gamalytic';
                        itemMetricUserGuide = `gamalytic__${originalMetricUserGuide}`;
                      } else {
                        // 如果没有找到 gamalytic 数据，创建空数据结构以显示空白卡片
                        displayItem = {
                          ...item,
                          baseIndicators: {},
                          numArrowData: {},
                          lineData: {},
                          rankData: {},
                          data_source: 'gamalytic',
                          metricUserGuide: `gamalytic__${originalMetricUserGuide}`,
                        };
                        itemDataSource = 'gamalytic';
                        itemMetricUserGuide = `gamalytic__${originalMetricUserGuide}`;
                      }
                    }

                    const titleInfoTipLocaleKey = getTitleInfoTipLocaleKey(itemMetricUserGuide);

                    return (
                      <Col span={8} key={index}>
                        <OverviewCard
                          key={`indicator${displayItem.titleContent}`}
                          className={classnames({
                            [COMPETITOR_GUIDE_CLASSNAMES.ELEMENT4]: displayItem?.metricUserGuide === 'digitalRevenue',
                          })}
                          yAxisInverse={isStoreRankMetric}
                          yAxisMin={isStoreRankMetric ? 1 : undefined}
                          enableSeriesAreaStyle={isStoreRankMetric ? true : !isDailyWishlistMetric}
                          noDataText={
                            !isStoreRankMetric
                              ? undefined
                              : (formatMessage({ id: storeRankNoDataTextKey }) as string)
                          }
                          titleContent={displayItem.titleContent}
                          titleIcon={displayItem.titleIcon}
                          baseIndicators={displayItem.baseIndicators}
                          numArrowData={displayItem.numArrowData}
                          lineData={displayItem.lineData}
                          lineDom={displayItem.lineDom}
                          rankData={displayItem.rankData}
                          index={index}
                          moduleName={moduleName}
                          entityType={entityType}
                          rankLoading={rankLoading}
                          setActiveId={setActiveId}
                          conditionDate={steamDateType}
                          conditionMarket={conditionMarket}
                          gameId={gameId}
                          // @ts-ignore
                          activeId={activeId}
                          cardItem={{
                            ...displayItem,
                            data_source: itemDataSource, // 使用替换后的 data_source
                            metricUserGuide: itemMetricUserGuide, // 使用替换后的 metric（带 gamalytic__ 前缀）
                          }}
                          setActiveSegment={setActiveSegment}
                          isOperateRegion={isOperateRegion}
                          isShowDataSourceIcon={false}
                          isShowRateTips={true}
                          isShowInfoIcon={!!titleInfoTipLocaleKey}
                          tipsInfoText={
                            titleInfoTipLocaleKey
                              ? formatMessage({ id: titleInfoTipLocaleKey })
                              : undefined
                          }
                        />
                      </Col>
                    );
                  })}
                  {/* SteamLineReviews卡片 */}
                  <Col span={8}>
                    {/* @ts-ignore */}
                    <OverviewCard
                      key={'indicatorSteamLineReviews'}
                      titleContent={'all_reviews'}
                      data_source={'custom_overview_card'}
                      customCardContent={getSteamLineReviews}
                      link_url={linkUrl}
                      isShowDataSourceIcon={false}
                    />
                  </Col>
                </Row>
                {/* Steam可视化柱状图区域 */}
                <CommonChart
                  gameId={gameId}
                  entityType={entityType}
                  kpiPlayerData={kpiPlayerData}
                  playersByCountry={kpiPlaytimeData}
                  steamLoading={steamLoading}
                  showNoDataMetricCard={showNoDataMetricCard}
                  setSteamDataHasEmptyLineData={setSteamDataHasEmptyLineData}
                />
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SteamDataContent;
